import { IS, nil, NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { RHProcurementPublic } from "../../../models/prodModels"
import { RHContractStore } from "../Core/RHContractStore"
import { RHCore } from "../Core/RHCore"
import { CBDataViewCellDescriptorWithObject } from "../Custom components/CB/CBDataView"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHAlertBannerView } from "../Custom components/RH/RHAlertBannerView"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { RHTableActionButtonView } from "../Custom components/RH/RHTableActionButtonView"
import { formatRHDate, formatRHDateValue } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHButtons } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementPartDisplay } from "../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHContractsViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { RHContractEntry } from "../SocketClientFunctions"
import { ContractEditDialogView } from "./ContractEditDialogView"
import { ProcurementRoundContext } from "../ProcurementDetails/ProcurementRoundContext"


export class RHContractsView extends UIView {
    
    _isProcurementEditableByCurrentUser = NO
    _procurement?: RHProcurementPublic
    _isCurrentUserOnProcuringSide = NO
    roundContext?: ProcurementRoundContext
    _contractLoadSequence = 0
    _procurementIdForLoadedContracts?: string

    readonly titleLabel = new UITextView(undefined, UITextView.type.header3)
        .configuredWithObject({
            text: "Lepingud",
            backgroundColor: UIColor.transparentColor,
            style: { fontWeight: "600" }
        })
        .addedAsSubviewToView(this)
    
    readonly contractsDataView = new RHDataView<RHContractEntry>()
        .configuredWithObject({
            tableView: { allRowsHaveEqualHeight: YES },
            descriptors: [
                CBDataViewCellDescriptorWithObject<RHContractEntry>({
                    title: "Hankija",
                    keyPath: "",
                    weight: RHContractsViewTokens.procurementDetailColumnWeights.procurer,
                    allowSorting: NO,
                    alignment: UITextView.textAlignment.left,
                    updateCell: cellView => {
                        cellView.titleLabel!.text = RHTextSanitizer.escapedHTML(
                            this._procurement?.procuringCompany?.name || "-"
                        )
                    }
                }),
                CBDataViewCellDescriptorWithObject<RHContractEntry>({
                    title: "Pakkuja",
                    keyPath: "offererCompany.name",
                    weight: RHContractsViewTokens.procurementDetailColumnWeights.contractor,
                    allowSorting: YES,
                    alignment: UITextView.textAlignment.left,
                    updateCell: (cellView, dataItem) => {
                        cellView.titleLabel!.text = dataItem.offererCompany?.name
                            ? RHTextSanitizer.escapedHTML(dataItem.offererCompany.name)
                            : "-"
                    }
                }),
                CBDataViewCellDescriptorWithObject<RHContractEntry>({
                    title: "Osad",
                    keyPath: "winningPartNumbers",
                    weight: RHContractsViewTokens.procurementDetailColumnWeights.parts,
                    allowSorting: NO,
                    alignment: UITextView.textAlignment.center,
                    updateCell: (cellView, dataItem) => {
                        if (!RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(
                            dataItem.procurementPartCount
                        )) {
                            cellView.titleLabel!.text = ""
                            return
                        }
                        const partNumbers = dataItem.winningPartNumbers ?? []
                        cellView.titleLabel!.text = partNumbers.length > 0
                            ? partNumbers.join(", ")
                            : "-"
                    }
                }),
                CBDataViewCellDescriptorWithObject<RHContractEntry>({
                    title: "Lepingu maksumus",
                    keyPath: "agreedAmount",
                    weight: RHContractsViewTokens.procurementDetailColumnWeights.value,
                    allowSorting: YES,
                    dataType: "number" as const,
                    alignment: UITextView.textAlignment.center,
                    updateCell: (cellView, dataItem) => {
                        if (dataItem.agreedAmount != null) {
                            cellView.titleLabel!.text = RHEuroFormatter.formattedCurrency(dataItem.agreedAmount, {
                                currency: dataItem.currency ?? "EUR",
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2
                            })
                        }
                        else {
                            cellView.titleLabel!.text = "-"
                        }
                    }
                }),
                CBDataViewCellDescriptorWithObject<RHContractEntry>({
                    title: "Otsuse kuupäev",
                    keyPath: "createdAt",
                    weight: RHContractsViewTokens.procurementDetailColumnWeights.decisionDate,
                    allowSorting: YES,
                    dataType: "string" as const,
                    alignment: UITextView.textAlignment.center,
                    updateCell: (cellView, dataItem) => {
                        if (dataItem.createdAt) {
                            cellView.titleLabel!.text = formatRHDate(new Date(dataItem.createdAt))
                        }
                        else {
                            cellView.titleLabel!.text = "-"
                        }
                    }
                }),
                CBDataViewCellDescriptorWithObject<RHContractEntry>({
                    title: "Lepingu nr",
                    keyPath: "contractNumber",
                    weight: RHContractsViewTokens.procurementDetailColumnWeights.contractNumber,
                    allowSorting: YES,
                    dataType: "string" as const,
                    alignment: UITextView.textAlignment.center,
                    updateCell: (cellView, dataItem) => {
                        cellView.titleLabel!.text = dataItem.contractNumber?.trim() || "-"
                    }
                }),
                CBDataViewCellDescriptorWithObject<RHContractEntry>({
                    title: "Lepingu sõlmimise kuupäev",
                    keyPath: "signedAt",
                    weight: RHContractsViewTokens.procurementDetailColumnWeights.contractDate,
                    allowSorting: YES,
                    dataType: "string" as const,
                    alignment: UITextView.textAlignment.center,
                    updateCell: (cellView, dataItem) => {
                        if (dataItem.signedAt) {
                            cellView.titleLabel!.text = formatRHDate(new Date(dataItem.signedAt))
                        }
                        else {
                            cellView.titleLabel!.text = "-"
                        }
                    }
                }),
                CBDataViewCellDescriptorWithObject<RHContractEntry>({
                    title: "Lepingu lõpptähtaeg",
                    keyPath: "contractEndDate",
                    weight: RHContractsViewTokens.procurementDetailColumnWeights.contractDate,
                    allowSorting: YES,
                    dataType: "string" as const,
                    alignment: UITextView.textAlignment.center,
                    updateCell: (cellView, dataItem) => {
                        cellView.titleLabel!.text = formatRHDateValue(dataItem.contractEndDate, "-")
                    }
                }),
                CBDataViewCellDescriptorWithObject<RHContractEntry, RHTableActionButtonView>({
                    title: "",
                    keyPath: "",
                    allowSorting: NO,
                    weight: RHContractsViewTokens.procurementDetailColumnWeights.status,
                    minimumWidth: RHContractsViewTokens.procurementDetailStatusActionWidth,
                    alignment: UITextView.textAlignment.center,
                    initCustomView: () => new RHTableActionButtonView().configuredWithObject({
                        preferredButtonWidth: RHContractsViewTokens.procurementDetailStatusActionWidth
                    }),
                    updateCell: (cellView, dataItem) => {
                        const actionButtonView = cellView.customView
                        actionButtonView.text = this._statusActionTitleForContract(dataItem)
                        actionButtonView.button.variant = this._statusActionVariantForContract(dataItem)
                        actionButtonView.button.enabled = this._contractProcurementIsEditableByCurrentUser(dataItem)
                        if (this._contractProcurementIsEditableByCurrentUser(dataItem)) {
                            actionButtonView.button.hoverText = "Sisesta või muuda lepingu andmeid"
                        }
                        else {
                            actionButtonView.button.hoverText = undefined
                        }
                        actionButtonView.buttonWasPressed = () => {
                            if (this._contractProcurementIsEditableByCurrentUser(dataItem)) {
                                this._openContractEditDialog(dataItem)
                            }
                        }
                    }
                })
            ]
        })
        .addedAsSubviewToView(this)
    
    readonly noContractsLabel = new RHAlertBannerView()
        .configuredWithObject({
            alertType: RHAlertBannerView.type.Warning,
            textPresentation: RHAlertBannerView.textPresentation.Outcome
        })
    
    constructor(elementID?: string) {
        super(elementID);
        (this as RHContractsView).configureWithObject({
            backgroundColor: RHColor.surface,
            style: {
                borderRadius: "8px",
                border: `1px solid ${RHColor.border.stringValue}`,
                boxShadow: `0 1px 3px ${RHColor.shadow.stringValue}`
            }
        })
        this.contractsDataView.configureMultilineTableHeader()
    }
    
    
    async loadContractsForProcurement(procurement: RHProcurementPublic, isCurrentUserOnProcuringSide: boolean, isProcurementEditableByCurrentUser: boolean) {
        const loadSequence = ++this._contractLoadSequence
        this._procurementIdForLoadedContracts = undefined
        this._isProcurementEditableByCurrentUser = isProcurementEditableByCurrentUser
        this._procurement = procurement
        this._isCurrentUserOnProcuringSide = isCurrentUserOnProcuringSide

        if (!procurement._id) {
            return
        }
        
        const procurementIds = new Set([procurement._id.toString()])
        if (this.roundContext?.hasRounds) {
            procurementIds.clear()
            this.roundContext.roundsWithViewableContracts.forEach(round => procurementIds.add(round.procurementId))
        }
        try {
            if (isCurrentUserOnProcuringSide) {
                const contractsByProcurement = await Promise.all([...procurementIds].map(procurementId =>
                    RHCore.sharedInstance.contractStore.retrieveContractsForProcurement(procurementId)
                ))
                if (loadSequence === this._contractLoadSequence) {
                    this._applyContracts(contractsByProcurement.flat())
                    this._procurementIdForLoadedContracts = procurement._id.toString()
                }
            }
            else {
                const contracts = await RHCore.sharedInstance.contractStore
                    .retrieveMyContractsForCurrentOfferer()
                // Filter down to contracts for this specific procurement
                const filtered = contracts.filter(
                    (contract: RHContractEntry) => procurementIds.has(contract.procurement._id)
                )
                if (loadSequence === this._contractLoadSequence) {
                    this._applyContracts(filtered)
                    this._procurementIdForLoadedContracts = procurement._id.toString()
                }
            }
        }
        catch (error) {
            console.error("Error loading contracts:", error)
        }
    }
    
    
    hasLoadedContractsForProcurement(procurement: RHProcurementPublic): boolean {
        return IS(procurement._id) && this._procurementIdForLoadedContracts === procurement._id.toString()
    }


    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        RHCore.sharedInstance.contractStore.subscribe(this, changedProcurementIds => {
            const procurement = this._procurement
            const procurementId = procurement?._id?.toString()
            if (
                procurement &&
                procurementId &&
                (changedProcurementIds.length === 0 || changedProcurementIds.includes(procurementId) ||
                    changedProcurementIds.some(changedId => this.roundContext?.informationByProcurementId.has(changedId)))
            ) {
                void this.loadContractsForProcurement(procurement, this._isCurrentUserOnProcuringSide, this._isProcurementEditableByCurrentUser)
            }
        })
    }


    override wasRemovedFromViewTree() {
        super.wasRemovedFromViewTree()
        RHCore.sharedInstance.contractStore.unsubscribe(this)
    }
    
    
    _applyContracts(contracts: RHContractEntry[]) {
        if (contracts.length === 0) {
            this.contractsDataView.data = []
            let noContractsText = "Selle hanke lepinguid ei leitud."
            if (!this._isCurrentUserOnProcuringSide) {
                noContractsText = "Sinu ettevõttega ei ole selles hankes lepingut sõlmitud."
            }
            this.noContractsLabel.text = noContractsText
            this.contractsDataView.removeFromSuperview()
            this.noContractsLabel.addedAsSubviewToView(this)
        }
        else {
            this.noContractsLabel.removeFromSuperview()
            this.contractsDataView.addedAsSubviewToView(this)
            this.contractsDataView.data = contracts.map(contract => {
                const round = this.roundContext?.roundsWithViewableContracts.find(candidate =>
                    candidate.procurementId === contract.procurement._id &&
                    candidate.procurementId !== this._procurement?._id?.toString()
                )
                if (!round || !this._procurement) {
                    return contract
                }
                const rootPart = this._procurement.parts.find(part => part._id.toString() === round.rootPartId)
                if (!rootPart) {
                    return contract
                }
                return {
                    ...contract,
                    winningPartNumbers: [rootPart.partNumber],
                    procurementPartCount: this._procurement.parts.length
                }
            })
            this.contractsDataView.noDataMessage = "Lepinguid ei leitud."
        }
        
        this.setNeedsLayoutUpToRootView()
    }
    
    
    _statusActionTitleForContract(entry: RHContractEntry) {
        if (entry.isCancelled) {
            if (entry.wasSignedBeforeCancellation) {
                return "Leping tühistatud"
            }
            return "Lepingut ei sõlmitud"
        }
        if (entry.completedAt) {
            return "Leping on lõpetatud"
        }
        if (entry.signedAt) {
            return "Leping on sõlmitud"
        }
        if (RHCore.sharedInstance.isInOffererMode) {
            return "Lepingu ootel"
        }
        return "Sõlmi leping"
    }

    _contractProcurementIsEditableByCurrentUser(contract: RHContractEntry): boolean {
        if (this.roundContext?.hasRounds) {
            const information = this.roundContext.informationByProcurementId.get(contract.procurement._id)
            return information?.isCurrentUserOnProcuringSide === YES && information.capabilities?.canEdit === YES
        }
        return this._isProcurementEditableByCurrentUser
    }


    _statusActionVariantForContract(entry: RHContractEntry) {
        if (entry.isCancelled) {
            if (entry.wasSignedBeforeCancellation) {
                return RHButtons.destructiveUtilityVariant
            }
            return RHButtons.exitUtilityVariant
        }
        if (entry.completedAt || entry.signedAt) {
            return "tableActionSuccess" as const
        }
        return RHButtons.tableActionVariant
    }


    _openContractEditDialog(entry: RHContractEntry) {
        const editView = new ContractEditDialogView()
        editView.populateFromEntry(entry)

        const shower = CBDialogViewShower.showDialog(editView, nil, NO)

        // noinspection JSConstantReassignment
        editView.saveButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            await editView.saveContractDetailsInDialog(shower, async () => {
                await this._saveContractDetails({ contractId: entry._id, ...editView.contractDetails })
            })
        }

        // noinspection JSConstantReassignment
        editView.cancelContractButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            if (!editView.allowsCancellation) {
                return
            }
            const nonConclusionReason = editView.enteredNonConclusionReason
            editView.replaceDialogWithCancellationConfirmation(
                shower,
                async () => {
                    await this._saveContractDetails({
                        contractId: entry._id,
                        isCancelled: YES,
                        nonConclusionReason
                    })
                }
            )
        }

        // noinspection JSConstantReassignment
        editView.completeContractButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            if (!editView.allowsCompletion) {
                return
            }
            shower.dialogView.dismiss()
            await this._markContractCompleted(entry._id)
        }

        // noinspection JSConstantReassignment
        editView.closeButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            shower.dialogView.dismiss()
        }
    }


    async _saveContractDetails(input: Parameters<RHContractStore["updateContractDetails"]>[0]) {
        const updatedEntry = await RHCore.sharedInstance.contractStore.updateContractDetails(input)
        this._replaceContractInData(updatedEntry)
    }


    async _markContractCompleted(contractId: string) {
        try {
            const updatedEntry = await RHCore.sharedInstance.contractStore.markContractCompleted(contractId)
            this._replaceContractInData(updatedEntry)
        }
        catch (error) {
            console.error("Error marking contract completed:", error)
            CBDialogViewShower.alert("Lepingu lõpetatuks märkimine ebaõnnestus.")
        }
    }


    _replaceContractInData(updatedEntry: RHContractEntry) {
        this._applyContracts(this.contractsDataView.data.map(item => {
            if (item._id === updatedEntry._id) {
                return updatedEntry
            }
            return item
        }))
    }


    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const bounds = this.contentBoundsWithInset(padding)
        
        bounds
            .rectangleWithHeight(labelHeight)
            .assignedAsFrameOfView(this.titleLabel)
        
        if (this.contractsDataView.superview === this) {
            this.titleLabel.frame
                .rectangleForNextRow(padding, 400)
                .assignedAsFrameOfView(this.contractsDataView)
        }
        else if (this.noContractsLabel.superview === this) {
            this.titleLabel.frame
                .rectangleForNextRow(padding, this.noContractsLabel)
                .assignedAsFrameOfView(this.noContractsLabel)
        }
    }
    
}
